import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Package,
  X,
  MagnifyingGlass,
  Printer,
  Minus,
  Plus,
  ArrowsClockwise,
  Cube,
  Stack,
  Warning,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { SlabHoldLabel } from "@/components/SlabHoldLabel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const KIND_WORD = { slab_piece: "Slab piece", slab: "Slab", remnant: "Remnant", sink: "Sink" };
const TABS = [
  ["slab", "Slabs in inventory"],
  ["remnant", "Remnants"],
  ["sink", "Sinks"],
];

// Manage the materials held/reserved for a project's quote: add slab/remnant/sink from
// inventory, change how many slabs are needed, swap a held slab and print a hold label.
export const ProjectMaterialManager = ({ quoteId, projectId, project, open, onClose, onChanged }) => {
  const navigate = useNavigate();
  const isProject = !quoteId;
  const base = quoteId ? `/quotes/${quoteId}/stock` : `/projects/${projectId}/stock`;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("slab");
  const [term, setTerm] = useState("");
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [printing, setPrinting] = useState(null); // array of holds to print as labels
  const [sinkQty, setSinkQty] = useState({});
  const [remnantSqft, setRemnantSqft] = useState({});

  const areaId = data?.areas?.[0]?.area_id || "";

  const context = {
    customer: project?.customer_company || project?.customer_name || "",
    title: project?.name || "Project",
    code: project?.code || "",
    address: project?.job_address || "",
    project_id: project?.id || "",
    install_date: project?.install_date || project?.deal?.install_date || "",
  };

  const load = useCallback(async () => {
    if (!quoteId && !projectId) return;
    try {
      const { data: d } = await api.get(base);
      setData(d);
    } catch {
      toast.error("Could not load materials for this project");
    }
  }, [quoteId, projectId, base]);

  useEffect(() => {
    if (open) {
      load();
      setKind("slab");
      setTerm("");
      setItems([]);
    }
  }, [open, load]);

  const search = useCallback(
    async (k, q) => {
      if (!quoteId && !projectId) return;
      setSearching(true);
      try {
        const { data: d } = await api.get(`${base}/inventory`, {
          params: { kind: k, q },
        });
        setItems(d.items || []);
      } catch {
        toast.error("Could not search inventory");
      } finally {
        setSearching(false);
      }
    },
    [quoteId, projectId, base],
  );

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(kind, term), 250);
    return () => clearTimeout(t);
  }, [open, kind, term, search]);

  const refresh = (d) => {
    setData(d);
    onChanged?.(d);
  };

  const hold = async (item) => {
    setBusy(true);
    try {
      const body =
        item.kind === "remnant"
          ? {
              area_id: areaId,
              kind: "remnant",
              ref_id: item.ref_id,
              sqft:
                Number(remnantSqft[item.ref_id]) > 0
                  ? Number(remnantSqft[item.ref_id])
                  : item.remaining_sqft || data?.areas?.[0]?.sqft || 0,
            }
          : item.kind === "sink"
            ? { area_id: areaId, kind: "sink", ref_id: item.ref_id, qty: Math.max(1, sinkQty[item.ref_id] || 1) }
            : { area_id: areaId, kind: item.kind, ref_id: item.ref_id, piece_id: item.piece_id || "" };
      const { data: d } = await api.post(`${base}/hold`, body);
      refresh(d);
      toast.success("Held for this project");
      search(kind, term);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not hold that");
    } finally {
      setBusy(false);
    }
  };

  const holdFromLayout = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/quotes/${quoteId}/stock/hold-layout`);
      refresh(d);
      const added = d.added?.length || 0;
      if (added) toast.success(`Reserved ${added} slab${added === 1 ? "" : "s"} from the layout`);
      else if (d.no_inventory) toast.message("Layout slabs aren't linked to inventory yet");
      else toast.message("Nothing new to reserve from the layout");
      if (d.skipped?.length) toast.error(`Skipped: ${d.skipped.join("; ")}`);
      search(kind, term);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reserve from layout");
    } finally {
      setBusy(false);
    }
  };

  const orderNeeded = async (item) => {    setBusy(true);
    try {
      const body =
        item.kind === "sink"
          ? { area_id: areaId, kind: "sink", sink_qty: 1, detail: `${item.brand} ${item.model} — special order to order` }
          : {
              area_id: areaId,
              kind: "material",
              slab_color: item.color || item.code || "",
              sqft: data?.areas?.[0]?.sqft || 0,
              detail: `${item.color || item.code} — special order to order`,
            };
      const { data: d } = await api.post(`/quotes/${quoteId}/stock/order-needed`, body);
      refresh(d);
      toast.success("Order-needed alert raised");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not raise the order");
    } finally {
      setBusy(false);
    }
  };

  const release = async (id) => {
    setBusy(true);
    try {
      const { data: d } = await api.delete(`${base}/hold/${id}`);
      refresh(d);
      toast.success("Released back to stock");
      search(kind, term);
    } catch {
      toast.error("Could not release that hold");
    } finally {
      setBusy(false);
    }
  };

  const swap = async (id) => {
    await release(id);
    setKind("slab");
    setTerm("");
    document.getElementById("material-manager-search")?.focus();
    toast.message("Now pick the replacement slab below");
  };

  const setNeeded = async (n) => {
    const count = Math.max(0, n);
    setBusy(true);
    try {
      const { data: d } = await api.patch(`/quotes/${quoteId}/stock/slabs-needed`, { count });
      refresh(d);
    } catch {
      toast.error("Could not update slabs needed");
    } finally {
      setBusy(false);
    }
  };

  const printLabels = (list) => {
    setPrinting(list);
    setTimeout(() => {
      document.body.classList.add("printing-piece");
      window.print();
      document.body.classList.remove("printing-piece");
    }, 300);
  };

  const holds = data?.holds || [];
  const labelableHolds = holds.filter(
    (h) => h.kind === "slab" || h.kind === "slab_piece" || h.kind === "remnant",
  );
  const slabsNeeded = data?.slabs_needed ?? 0;
  const slabsHeld = data?.slabs_held ?? 0;
  const poHref = (it) =>
    `/inventory/purchase-orders/new?project=${project?.id || ""}` +
    `&slab_color=${encodeURIComponent(it.color || it.code || "")}` +
    `${project?.customer_id ? `&customer=${project.customer_id}` : ""}`;
  const btn =
    "rounded-md border border-zinc-700 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:bg-indigo-500 hover:text-white disabled:opacity-40";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-h-[90vh] w-[min(96vw,62rem)] max-w-none overflow-y-auto border-zinc-800 bg-zinc-950 text-zinc-100"
        data-testid="project-material-manager"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Package size={18} weight="bold" className="text-indigo-300" /> Manage materials
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Hold a slab, remnant or sink for this project. In-stock items are reserved right away;
            special-order items raise an "order needed" alert. Print a hold label for the shop floor.
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <p className="py-8 text-center text-sm text-zinc-500">Checking inventory…</p>
        ) : (
          <div className="space-y-4">
            {/* SLABS NEEDED + status */}
            {!isProject && (
            <div
              className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-3"
              data-testid="material-slabs-needed"
            >
              <Cube size={16} weight="bold" className="text-indigo-300" />
              <span className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                Slabs needed
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  data-testid="material-slabs-needed-dec"
                  disabled={busy || slabsNeeded <= 0}
                  onClick={() => setNeeded(slabsNeeded - 1)}
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-zinc-700 text-zinc-200 hover:border-indigo-500 disabled:opacity-40"
                >
                  <Minus size={13} weight="bold" />
                </button>
                <span
                  data-testid="material-slabs-needed-value"
                  className="font-mono-data w-8 text-center text-lg font-bold text-zinc-100"
                >
                  {slabsNeeded}
                </span>
                <button
                  type="button"
                  data-testid="material-slabs-needed-inc"
                  disabled={busy}
                  onClick={() => setNeeded(slabsNeeded + 1)}
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-zinc-700 text-zinc-200 hover:border-indigo-500 disabled:opacity-40"
                >
                  <Plus size={13} weight="bold" />
                </button>
              </div>
              <span
                data-testid="material-held-count"
                className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                  slabsHeld >= slabsNeeded && slabsNeeded > 0
                    ? "bg-emerald-500/15 text-emerald-200"
                    : "bg-amber-500/15 text-amber-200"
                }`}
              >
                {slabsHeld} slab{slabsHeld === 1 ? "" : "s"} held
              </span>
            </div>
            )}

            {/* COVERAGE CHECK — needed vs held sq ft per area */}
            {(data.areas || []).length > 0 && (
              <div
                className="space-y-1.5 rounded-lg border border-zinc-800 bg-zinc-900 p-3"
                data-testid="material-coverage"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Coverage — held vs needed
                </div>
                {data.areas.map((a) => {
                  const covered = (a.held_sqft || 0) >= (a.sqft || 0) - 0.5 && (a.sqft || 0) > 0;
                  return (
                    <div
                      key={a.area_id}
                      data-testid={`material-coverage-${a.area_id}`}
                      className="flex flex-wrap items-center gap-2 text-xs"
                    >
                      <span className="font-semibold text-zinc-200">{a.name || "Area"}</span>
                      <span className="font-mono-data text-zinc-400">
                        {a.sqft || 0} sf needed · {a.held_sqft || 0} sf held
                      </span>
                      <span
                        data-testid={`material-coverage-state-${a.area_id}`}
                        className={`ml-auto rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${
                          covered
                            ? "bg-emerald-500/15 text-emerald-200"
                            : "bg-amber-500/15 text-amber-200"
                        }`}
                      >
                        {covered
                          ? "Covered"
                          : `Short ${Math.max(0, Math.round((a.sqft || 0) - (a.held_sqft || 0)))} sf`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ORDER-NEEDED alerts */}
            {data.order_alerts?.length > 0 && (
              <div
                className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200"
                data-testid="material-order-alerts"
              >
                <span className="font-bold uppercase tracking-widest">Order needed</span>
                <ul className="mt-1 list-disc pl-4">
                  {data.order_alerts.map((o) => (
                    <li key={o.id}>{o.detail}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* CURRENT HOLDS */}
            <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-3" data-testid="material-holds">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                  Reserved for this project
                </span>
                {labelableHolds.length > 0 && (
                  <button
                    type="button"
                    data-testid="material-print-all-labels"
                    onClick={() => printLabels(labelableHolds)}
                    className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-full border border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
                  >
                    <Printer size={13} weight="bold" /> Print all {labelableHolds.length} labels
                  </button>
                )}
                {!isProject && (
                <button
                  type="button"
                  data-testid="material-hold-from-layout"
                  disabled={busy}
                  onClick={holdFromLayout}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full border border-indigo-500/50 bg-indigo-500/10 px-3 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20 disabled:opacity-40"
                >
                  <Stack size={13} weight="bold" /> Reserve slabs from drawing layout
                </button>
                )}
              </div>
              {holds.length === 0 ? (
                <p className="text-xs text-zinc-500" data-testid="material-holds-empty">
                  Nothing held yet — add a slab, remnant or sink below.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {holds.map((h) => (
                    <li
                      key={h.id}
                      className="flex flex-wrap items-center gap-2 rounded-md bg-zinc-950 px-2 py-1.5 text-xs"
                      data-testid={`material-hold-${h.id}`}
                    >
                      <span className="rounded bg-indigo-500/20 px-1.5 text-[9px] font-bold uppercase tracking-widest text-indigo-200">
                        {KIND_WORD[h.kind] || h.kind}
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        {h.label}
                        {h.length_in && h.width_in ? (
                          <span className="ml-2 text-zinc-500">
                            {h.length_in}" × {h.width_in}"
                          </span>
                        ) : null}
                      </span>
                      {(h.kind === "slab" || h.kind === "slab_piece" || h.kind === "remnant") && (
                        <button
                          type="button"
                          data-testid={`material-hold-label-${h.id}`}
                          onClick={() => printLabels([h])}
                          className="flex items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:border-indigo-500 hover:text-indigo-200"
                        >
                          <Printer size={12} weight="bold" /> Label
                        </button>
                      )}
                      {(h.kind === "slab" || h.kind === "slab_piece") && (
                        <button
                          type="button"
                          data-testid={`material-hold-swap-${h.id}`}
                          disabled={busy}
                          onClick={() => swap(h.id)}
                          className="flex items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:border-indigo-500 hover:text-indigo-200 disabled:opacity-40"
                        >
                          <ArrowsClockwise size={12} weight="bold" /> Swap
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid={`material-hold-release-${h.id}`}
                        disabled={busy}
                        onClick={() => release(h.id)}
                        className="text-zinc-500 hover:text-red-300"
                      >
                        <X size={13} weight="bold" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* ADD MATERIAL PICKER */}
            <section className="rounded-lg border border-indigo-500/30 bg-zinc-900 p-3" data-testid="material-add">
              <div className="mb-2 text-xs font-bold uppercase tracking-widest text-indigo-200">
                Add material
              </div>
              <div className="flex flex-wrap gap-1.5">
                {TABS.map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`material-add-tab-${k}`}
                    onClick={() => {
                      setKind(k);
                      setItems([]);
                    }}
                    className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                      kind === k
                        ? "bg-indigo-500 text-white"
                        : "border border-zinc-700 text-zinc-300 hover:border-indigo-500"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="relative mt-2">
                <MagnifyingGlass
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <input
                  id="material-manager-search"
                  data-testid="material-search-input"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder={`Search ${kind === "sink" ? "sinks" : kind === "remnant" ? "remnants" : "slabs"} by name, color, code…`}
                  className="h-10 w-full rounded-md border border-zinc-800 bg-zinc-950 pl-9 pr-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
                  style={{ fontSize: "16px" }}
                />
              </div>

              <div className="mt-2 max-h-80 space-y-1.5 overflow-y-auto" data-testid="material-results">
                {searching && <p className="py-3 text-center text-xs text-zinc-500">Searching…</p>}
                {!searching && items.length === 0 && (
                  <p className="py-3 text-center text-xs text-zinc-500" data-testid="material-results-empty">
                    Nothing found — try a different search.
                  </p>
                )}
                {items.map((it, i) => (
                  <div
                    key={`${it.ref_id}-${it.piece_id || i}`}
                    data-testid={`material-result-${i}`}
                    className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-2.5 py-2 text-xs"
                  >
                    <div className="min-w-0 flex-1">
                      {it.kind === "sink" ? (
                        <>
                          <span className="block font-bold text-zinc-100">
                            {it.brand} {it.model}
                          </span>
                          <span className="block text-[10px] text-zinc-500">
                            {[it.sink_type, it.size].filter(Boolean).join(" · ")}
                            {it.special_order ? " · Special order" : ` · ${it.quantity} on hand`}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="block font-bold text-zinc-100">
                            {it.color || it.code}
                            <span className="ml-2 font-mono-data text-[10px] text-zinc-500">{it.code}</span>
                          </span>
                          <span className="block text-[10px] text-zinc-500">
                            {[it.material, it.serial].filter(Boolean).join(" · ")}
                            {it.length_in && it.width_in ? ` · ${it.length_in}" × ${it.width_in}"` : ""}
                            {it.remaining_sqft ? ` · ${it.remaining_sqft} sf left` : ""}
                            {it.location ? ` · ${it.location}` : ""}
                          </span>
                        </>
                      )}
                    </div>
                    {it.low_stock ? (
                      <span
                        data-testid={`material-result-low-${i}`}
                        title={`Only ${it.on_hand} of this color on hand`}
                        className="flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-red-300"
                      >
                        <Warning size={11} weight="fill" /> Low stock
                      </span>
                    ) : null}
                    {it.special_order ? (
                      <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-amber-300">
                        Special order
                      </span>
                    ) : null}
                    {it.kind !== "sink" && (it.low_stock || it.special_order) && (
                      <button
                        type="button"
                        data-testid={`material-result-po-${i}`}
                        onClick={() => navigate(poHref(it))}
                        className="rounded-md border border-indigo-500/50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
                      >
                        Create PO
                      </button>
                    )}
                    {it.kind === "sink" && it.available && (
                      <div className="flex items-center gap-1" data-testid={`material-sink-qty-${i}`}>
                        <button
                          type="button"
                          data-testid={`material-sink-qty-dec-${i}`}
                          onClick={() =>
                            setSinkQty((q) => ({ ...q, [it.ref_id]: Math.max(1, (q[it.ref_id] || 1) - 1) }))
                          }
                          className="flex h-6 w-6 items-center justify-center rounded border border-zinc-700 text-zinc-200 hover:border-indigo-500"
                        >
                          <Minus size={11} weight="bold" />
                        </button>
                        <span className="font-mono-data w-5 text-center text-xs font-bold text-zinc-100">
                          {sinkQty[it.ref_id] || 1}
                        </span>
                        <button
                          type="button"
                          data-testid={`material-sink-qty-inc-${i}`}
                          onClick={() =>
                            setSinkQty((q) => ({
                              ...q,
                              [it.ref_id]: Math.min(it.quantity || 1, (q[it.ref_id] || 1) + 1),
                            }))
                          }
                          className="flex h-6 w-6 items-center justify-center rounded border border-zinc-700 text-zinc-200 hover:border-indigo-500"
                        >
                          <Plus size={11} weight="bold" />
                        </button>
                      </div>
                    )}
                    {it.kind === "remnant" && it.available && (
                      <div
                        className="flex items-center gap-1"
                        data-testid={`material-remnant-sqft-${i}`}
                      >
                        <input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={remnantSqft[it.ref_id] ?? it.remaining_sqft ?? ""}
                          onChange={(e) =>
                            setRemnantSqft((m) => ({ ...m, [it.ref_id]: e.target.value }))
                          }
                          data-testid={`material-remnant-sqft-input-${i}`}
                          className="font-mono-data h-6 w-16 rounded border border-zinc-700 bg-zinc-900 px-1.5 text-center text-xs text-zinc-100 focus:border-indigo-500 focus:outline-none"
                        />
                        <span className="text-[10px] text-zinc-500">sf</span>
                      </div>
                    )}
                    {it.available ? (
                      <button
                        type="button"
                        data-testid={`material-result-hold-${i}`}
                        disabled={busy}
                        onClick={() => hold(it)}
                        className={btn}
                      >
                        Hold
                      </button>
                    ) : !isProject ? (
                      <button
                        type="button"
                        data-testid={`material-result-order-${i}`}
                        disabled={busy}
                        onClick={() => orderNeeded(it)}
                        className="rounded-md border border-amber-500/50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-200 transition-colors duration-150 hover:bg-amber-500/20 disabled:opacity-40"
                      >
                        Need to order
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>

            {/* LABEL PRINT PREVIEW */}
            {printing && (
              <div id="piece-print-area" className="mt-2" data-testid="material-label-print-area">
                <div className="no-print mb-2 flex items-center justify-between gap-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Label preview — {printing.length} label{printing.length === 1 ? "" : "s"} · 4 × 6 in
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="material-label-print-again"
                      onClick={() => printLabels(printing)}
                      className="flex h-9 items-center gap-1.5 rounded-lg bg-indigo-500 px-3.5 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
                    >
                      <Printer size={13} weight="bold" /> Print
                    </button>
                    <button
                      type="button"
                      data-testid="material-label-close"
                      onClick={() => setPrinting(null)}
                      className="flex h-9 items-center rounded-lg border border-zinc-700 px-3.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                    >
                      Close
                    </button>
                  </div>
                </div>
                <div className="label-stack space-y-4">
                  {printing.map((h) => (
                    <div
                      key={h.id}
                      data-testid={`material-label-wrap-${h.id}`}
                      className="origin-top-left scale-[0.72] sm:scale-100"
                      style={{ width: "4in" }}
                    >
                      <SlabHoldLabel hold={h} context={context} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
